import React, { useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import styles from 'styles/pages/SurveyBuilder.module.css';
import { SurveyNameHeaderEditable } from 'components/SurveyNameHeaderEditable';
import Tabs from '@ax2/common/components/tab/Tabs';
import { useSurveyContext } from '../contexts/SurveyContext';
import { EditSurveyParamType, RouterParams } from 'router/RouterParams';
import { redirect } from 'helpers/router';
import { getSurveys, storeSurvey } from 'rest/makeSurveyClient';
import { isScreenersValid, getSurveyErrors } from 'helpers/survey';
import { QuestionBuildersList } from 'components/question-builders/QuestionBuildersList';
import { ScreenerBuildersList } from 'components/question-builders/ScreenerBuildersList';
import HTML5Backend from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import DefaultPage from './DefaultPage';
import { PreviewAndSend } from 'components/PreviewAndSend';
import Loading from '@ax2/common/components/Loading';
import { getErrorMessage } from '@ax2/common/helpers/error';
import { RestError } from '@ax2/common/rest/baseClient';
import { useErrorUpdateContext } from '@ax2/common/contexts/ErrorContext';
import { getIcon, saveIcon } from 'rest/shopClient';

enum SurveyBuilderType {
  ScreenerQuestions = 'Screener questions',
  SurveyQuestions = 'Survey questions',
  PreviewAndSend = 'Preview & Send',
}

type SurveyBuilderData = RouteComponentProps<RouterParams>;

const SurveyBuilder: React.FC<SurveyBuilderData> = ({ history, match }) => {
  const [isSaving, setIsSaving] = useState(false);
  const surveySchema = useSurveyContext();
  const updateError = useErrorUpdateContext();
  const surveyType = getSurveyTypeFromRouterParam(match.params.editSurveyType);
  const isSurveyValid = getSurveyErrors(surveySchema).length === 0;
  const isSurveyScreenersValid = isScreenersValid(surveySchema);
  const [color3, setcolor3] = useState('false');

  const handleTabChange = async (surveyType: SurveyBuilderType) => {
    switch (surveyType) {
      case SurveyBuilderType.ScreenerQuestions:
        redirect('/surveyBuilder/' + getRouterParamFromSurveyType(surveyType));
        break;
      case SurveyBuilderType.SurveyQuestions:
        if (surveySchema && isSurveyScreenersValid) {
          redirect(
            '/surveyBuilder/' + getRouterParamFromSurveyType(surveyType)
          );
        }
        break;
      case SurveyBuilderType.PreviewAndSend:
        if (surveySchema && isSurveyValid) {
          setIsSaving(true);
          await storeSurvey(surveySchema, true)
            .then(() => {
              setIsSaving(false);
              redirect(
                '/surveyBuilder/' + getRouterParamFromSurveyType(surveyType)
              );
            })
            .catch(e => {
              setIsSaving(false);
              updateError({
                text: getErrorMessage(e as RestError),
              });
            });
          const surveys1 = await getSurveys();

          for (let k = 0; k < surveys1.length; k++) {
            if (surveys1[k].id === surveySchema.id) {
              surveySchema.status = surveys1[k].status;
            }
          }
        }
    }
  };

  const surveyQuestions = (
    <QuestionBuildersList
      onFinish={() => handleTabChange(SurveyBuilderType.PreviewAndSend)}
      colorFunction={() => {
        if (surveySchema) {
          saveIcon(`color2+${surveySchema.id}`, 'true');
        }
      }}
      setcolor3false={() => {
        setcolor3('false');
      }}
    />
  );

  const surveyScreeners = (
    <ScreenerBuildersList
      onFinish={() => handleTabChange(SurveyBuilderType.SurveyQuestions)}
      colorFunction={() => {
        if (surveySchema) {
          saveIcon(`color1+${surveySchema.id}`, 'true');
        }
      }}
      setcolor3false={() => {
        setcolor3('false');
      }}
    />
  );

  const previewAndSendContent = !isSaving ? (
    <PreviewAndSend
      colorFunction={async () => {
        if (surveySchema) {
          saveIcon(`color3+${surveySchema.id}`, 'true');
          saveIcon(`color2+${surveySchema.id}`, 'true');
          saveIcon(`color1+${surveySchema.id}`, 'true');
        }
        setcolor3('true');
      }}
      setcolor3false={() => {
        setcolor3('false');
      }}
      setcolor3true={() => {
        setcolor3('true');
      }}
    />
  ) : (
    <Loading paddingVertical={30} />
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <DefaultPage
        header={{
          title: 'Survey Builder',
          subtitle: 'This is where you edit, preview, and send your surveys.',
          backAction: {
            text: 'BACK TO DASHBOARD',
            onCompletion: () => history.push('/dashboard'),
          },
        }}
        wideContent={true}
      >
        <div className={styles.container}>
          <SurveyNameHeaderEditable
            isEditable={surveyType !== SurveyBuilderType.PreviewAndSend}
          />
          <Tabs
            activeTab={surveyType}
            onTabChange={t => handleTabChange(t as SurveyBuilderType)}
            tabs={[
              {
                name: SurveyBuilderType.ScreenerQuestions,
                label: SurveyBuilderType.ScreenerQuestions.toUpperCase(),
                content: surveyScreeners,
                isCompleted: surveySchema
                  ? getIcon(`color1+${surveySchema.id}`)
                  : 'false',
              },
              {
                name: SurveyBuilderType.SurveyQuestions,
                label: SurveyBuilderType.SurveyQuestions.toUpperCase(),
                content: surveyQuestions,
                isCompleted: surveySchema
                  ? getIcon(`color2+${surveySchema.id}`)
                  : 'false',
              },
              {
                name: SurveyBuilderType.PreviewAndSend,
                label: SurveyBuilderType.PreviewAndSend.toUpperCase(),
                content: previewAndSendContent,
                isCompleted:
                  color3 ||
                  (surveySchema
                    ? getIcon(`color3+${surveySchema.id}`)
                    : color3),
              },
            ]}
          />
        </div>
      </DefaultPage>
    </DndProvider>
  );
};

export default withRouter(SurveyBuilder);

function getSurveyTypeFromRouterParam(
  type?: EditSurveyParamType
): SurveyBuilderType {
  switch (type) {
    case 'screener':
      return SurveyBuilderType.ScreenerQuestions;
    case 'questions':
      return SurveyBuilderType.SurveyQuestions;
    case 'preview':
      return SurveyBuilderType.PreviewAndSend;
    default:
      return SurveyBuilderType.SurveyQuestions;
  }
}
function getRouterParamFromSurveyType(
  type: SurveyBuilderType
): EditSurveyParamType {
  switch (type) {
    case SurveyBuilderType.ScreenerQuestions:
      return 'screener';
    case SurveyBuilderType.SurveyQuestions:
      return 'questions';
    case SurveyBuilderType.PreviewAndSend:
      return 'preview';
    default:
      return 'questions';
  }
}
